import { motion, useInView, useReducedMotion } from 'framer-motion';
import dynamic from 'next/dynamic';
import React, { useRef } from 'react';

import resolveMotionEase from '@/components/shared/animations/motion/resolveMotionEase';

type Props = {
  title: string;
  subtitle?: React.ReactNode;
};

// Lottie Player는 SSR 환경에서 document를 참조하므로 CSR에서만 로드되도록 처리
const Player = dynamic<any>(
  () => import('@lottiefiles/react-lottie-player').then((m) => m.Player),
  { ssr: false },
);

export default function AnimationSectionTitle({ title, subtitle }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const shouldLoadPlayer = useInView(containerRef, {
    once: true,
    margin: '300px 0px',
  });
  const isInView = useInView(titleRef, {
    once: true,
    margin: '0px 0px -30% 0px',
  });
  const shouldReduceMotion = useReducedMotion();
  const duration = shouldReduceMotion ? 0 : 1.25;
  const ease = resolveMotionEase('power1.out');

  return (
    <div className="text-center" ref={containerRef}>
      <div className="mx-auto h-[60px] w-[60px] lg:h-[110px] lg:w-[110px]">
        {shouldLoadPlayer && (
          <Player
            className="h-full w-full"
            autoplay
            loop
            src="/images/apps/common/AnimationSectionTitle_lottie_01.json"
          />
        )}
      </div>
      <div
        className="overflow-hidden [transform-style:preserve-3d]"
        ref={titleRef}
      >
        {/*
         * transform / opacity 초기값은 globals.css 의
         * .animation-section-title__title 에 남겨둔다.
         * Tailwind 유틸리티는 전부 !important 라서 클래스로 옮기면
         * framer-motion 이 인라인 style 로 쓰는 값을 이겨 애니메이션이 멈춘다.
         */}
        <motion.h2
          className="animation-section-title__title mt-[16px] font-dmSans text-[clamp(52px,7.2917vw,140px)] font-bold leading-[100%] tracking-[-1.04px] text-apps-coreGrayBlack lg:mt-[24px] lg:tracking-[-2.8px]"
          initial={{
            opacity: 0,
            rotateX: -90,
            scale: 0.7,
            transformPerspective: 600,
            y: '130%',
          }}
          animate={
            isInView ? { opacity: 1, rotateX: 0, scale: 1, y: 0 } : undefined
          }
          transition={{ duration, ease }}
        >
          {title}
        </motion.h2>
      </div>
      <motion.h3
        className="animation-section-title__subtitle mt-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-coreGray01 lg:mt-[24px] lg:text-[22px] lg:tracking-[-0.44px]"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : undefined}
        transition={{ delay: shouldReduceMotion ? 0 : 0.5, duration, ease }}
      >
        {subtitle}
      </motion.h3>
    </div>
  );
}
